"use client";

import React, { useCallback } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { Switch, Text } from "@lmn/design-system";
import IconCircleInfo from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCircleInfo.js";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import ResponsiveTooltip from "@/components/UI/Tooltip/ResponsiveTooltip";

const FilterWrapper = styled.div<{ $onWhiteBackground?: boolean }>`
  width: 100%;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: ${({ $onWhiteBackground, theme }) =>
    $onWhiteBackground
      ? theme.kallisto.color.background.static.backgroundLayer01
      : theme.kallisto.color.background.static.backgroundPage};
  padding: 1rem;
  border-radius: 8px;

  & label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
`;

interface SpecialDealsFilterProps {
  delayedUpdate?: boolean;
  onWhiteBackground?: boolean;
}

const SpecialDealsFilter: React.FC<SpecialDealsFilterProps> = ({
  delayedUpdate = false,
  onWhiteBackground = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();

  // Derive state directly from URL/tempFilters - no local state needed
  const isSpecialDeals = delayedUpdate
    ? tempFilters.get("specialDeals") === "true"
    : searchParams.get("specialDeals") === "true";

  const handleSpecialDealsChange = useCallback(
    (checked: boolean) => {
      if (delayedUpdate) {
        updateTempFilter("specialDeals", checked ? "true" : null);
      } else {
        updateFilter("specialDeals", checked ? "true" : null);
      }
    },
    [updateFilter, updateTempFilter, delayedUpdate]
  );

  return (
    <FilterWrapper $onWhiteBackground={onWhiteBackground}>
      <Switch
        checked={isSpecialDeals}
        onChange={handleSpecialDealsChange}
        ltr={true}
        // @ts-expect-error: change this in design system so that it accept React Components
        label={
          <Label>
            <Text variant="BodySmall">Special Deals</Text>
            <ResponsiveTooltip
              content="Auswahl an unschlagbaren Angeboten, die nur für kurze Zeit verfügbar sind."
              trigger={<IconCircleInfo size={14} />}
            />
          </Label>
        }
        aria-describedby="special-deals-description"
        data-testid="special-deals-switch"
      />
      <span id="special-deals-description" className="sr-only">
        Wählen Sie aus, ob Sie nur Angebote mit Sonderpreisen sehen möchten.
      </span>
    </FilterWrapper>
  );
};

export default SpecialDealsFilter;
